import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type Organigrama = components['schemas']['OrganigramaRead']

export function useOrganigrama(businessId: string | undefined) {
  return useQuery({
    queryKey: ['organigrama', businessId],
    queryFn: async () => (await api.get<Organigrama>(`/businesses/${businessId}/organigrama`)).data,
    enabled: !!businessId,
  })
}

// The auditor's register, distinct from the client-only diagram PNG.
export function organigramaExportUrl(businessId: string): string {
  return `/businesses/${businessId}/organigrama/export.xlsx`
}

// The PATCH replaces the whole áreas list, so the caller must keep the person's other áreas.
export function useMoveEmployeeArea(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({employeeId, areaNames}: {employeeId: string; areaNames: string[]}) =>
      (
        await api.patch(`/businesses/${businessId}/employees/${employeeId}`, {
          areas: areaNames,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['organigrama', businessId]})
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['tags', businessId]})
    },
  })
}

// Replaces the whole positions list with the single chosen name: each node shows one cargo.
export function useSetEmployeePosition(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({employeeId, positionNames}: {employeeId: string; positionNames: string[]}) =>
      (
        await api.patch(`/businesses/${businessId}/employees/${employeeId}`, {
          positions: positionNames,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['organigrama', businessId]})
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['tags', businessId]})
    },
  })
}
